import React, { useState, useEffect } from "react";
import { 
    Card, Row, Col, Form, Button, Alert, Spinner, Badge, Modal 
} from "react-bootstrap";
import { Link, useNavigate, useParams } from "react-router-dom";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import './Produits.css';

export default function ModifierProduit() {
    const navigate = useNavigate();
    const { id } = useParams();
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleting, setDeleting] = useState(false);
    
    const [produit, setProduit] = useState({
        reference: '',
        code_barre: '',
        designation: '',
        description: '',
        id_categorie: '',
        unite_mesure: 'PCE',
        prix_achat_ht: 0,
        prix_vente_ht: 0,
        tva_taux: 18,
        css_taux: 1,
        seuil_alerte: 5,
        stock_minimum: 0,
        stock_maximum: '',
        emplacement: '',
        actif: 1
    });

    useEffect(() => {
        fetchProduit();
        fetchCategories();
    }, [id]);

    const fetchProduit = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            const response = await fetch(
                `https://sawd.julesferry-ga.com/api/produits.php?id=${id}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );

            if (!response.ok) throw new Error("Erreur chargement");
            
            const result = await response.json();
            setProduit(result.data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchCategories = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                'https://sawd.julesferry-ga.com/api/categories.php',
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (response.ok) {
                const result = await response.json();
                setCategories(result.data || []);
            }
        } catch (err) {
            console.error("Erreur chargement catégories", err);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!produit.designation) {
            setError("La désignation est obligatoire");
            return;
        }

        setSaving(true);
        setError("");

        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `https://sawd.julesferry-ga.com/api/produits.php?id=${id}`,
                {
                    method: 'PUT',
                    headers: { 
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(produit)
                }
            );

            const result = await response.json();

            if (result.success) {
                setSuccess("Produit mis à jour avec succès !");
                setTimeout(() => {
                    navigate('/stock/produits');
                }, 2000);
            } else {
                setError(result.message || "Erreur lors de la mise à jour");
            }
        } catch (err) {
            setError("Erreur de connexion au serveur");
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        setDeleting(true);
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `https://sawd.julesferry-ga.com/api/produits.php?id=${id}`,
                {
                    method: 'DELETE',
                    headers: { 'Authorization': `Bearer ${token}` }
                }
            );

            const result = await response.json();

            if (result.success) {
                setShowDeleteModal(false);
                setSuccess("Produit supprimé avec succès !");
                setTimeout(() => {
                    navigate('/stock/produits');
                }, 2000);
            } else {
                setError(result.message);
                setShowDeleteModal(false);
            }
        } catch (err) {
            setError("Erreur lors de la suppression");
            setShowDeleteModal(false);
        } finally {
            setDeleting(false);
        }
    };

    if (loading) {
        return (
            <React.Fragment>
                <Header />
                <div className="main main-app p-3 p-lg-4">
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
                        <Spinner animation="border" variant="primary" />
                    </div>
                </div>
            </React.Fragment>
        );
    }

    return (
        <React.Fragment>
            <Header />
            <div className="main main-app p-3 p-lg-4">
                <div className="container-fluid">
                    {/* En-tête */}
                    <Row className="mb-4">
                        <Col md={12}>
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <ol className="breadcrumb fs-sm mb-1">
                                        <li className="breadcrumb-item"><Link to="/dashboard">Tableau de bord</Link></li>
                                        <li className="breadcrumb-item"><Link to="/stock">Stock</Link></li>
                                        <li className="breadcrumb-item"><Link to="/stock/produits">Produits</Link></li>
                                        <li className="breadcrumb-item active">Modifier produit</li>
                                    </ol>
                                    <h4 className="main-title mb-0">
                                        <i className="ri-pencil-line text-primary me-2"></i>
                                        Modifier le produit
                                    </h4>
                                </div>
                            </div>
                        </Col>
                    </Row>

                    {error && (
                        <Alert variant="danger" onClose={() => setError("")} dismissible>
                            <i className="ri-error-warning-line me-2"></i>
                            {error}
                        </Alert>
                    )}
                    
                    {success && (
                        <Alert variant="success">
                            <i className="ri-checkbox-circle-line me-2"></i>
                            {success}
                        </Alert>
                    )}

                    <Row className="justify-content-center">
                        <Col lg={8}>
                            <Card className="border-0 shadow-sm">
                                <Card.Header className="bg-primary text-white d-flex justify-content-between align-items-center">
                                    <h5 className="mb-0">
                                        <i className="ri-information-line me-2"></i>
                                        Informations du produit
                                    </h5>
                                    <Badge bg="light" text="dark" className="px-3 py-2">
                                        {produit.reference}
                                    </Badge>
                                </Card.Header>
                                <Card.Body>
                                    <Form onSubmit={handleSubmit}>
                                        <Row className="g-3">
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label>Référence</Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        value={produit.reference}
                                                        disabled
                                                        className="bg-light"
                                                    />
                                                    <Form.Text className="text-muted">
                                                        La référence ne peut pas être modifiée
                                                    </Form.Text>
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label>Code-barres</Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        value={produit.code_barre || ''}
                                                        onChange={(e) => setProduit({...produit, code_barre: e.target.value})}
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={12}>
                                                <Form.Group>
                                                    <Form.Label>Désignation <span className="text-danger">*</span></Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        value={produit.designation}
                                                        onChange={(e) => setProduit({...produit, designation: e.target.value})}
                                                        required
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={12}>
                                                <Form.Group>
                                                    <Form.Label>Description</Form.Label>
                                                    <Form.Control
                                                        as="textarea"
                                                        rows={3}
                                                        value={produit.description || ''}
                                                        onChange={(e) => setProduit({...produit, description: e.target.value})}
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label>Catégorie</Form.Label>
                                                    <Form.Select
                                                        value={produit.id_categorie || ''}
                                                        onChange={(e) => setProduit({...produit, id_categorie: e.target.value})}
                                                    >
                                                        <option value="">Sélectionner une catégorie</option>
                                                        {categories.map(cat => (
                                                            <option key={cat.id_categorie} value={cat.id_categorie}>
                                                                {cat.nom_categorie}
                                                            </option>
                                                        ))}
                                                    </Form.Select>
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label>Unité de mesure</Form.Label>
                                                    <Form.Select
                                                        value={produit.unite_mesure}
                                                        onChange={(e) => setProduit({...produit, unite_mesure: e.target.value})}
                                                    >
                                                        <option value="PCE">Pièce</option>
                                                        <option value="KG">Kilogramme</option>
                                                        <option value="L">Litre</option>
                                                        <option value="M">Mètre</option>
                                                        <option value="CART">Carton</option>
                                                        <option value="PAL">Palette</option>
                                                    </Form.Select>
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label>Prix d'achat HT</Form.Label>
                                                    <Form.Control
                                                        type="number"
                                                        step="0.01"
                                                        min="0"
                                                        value={produit.prix_achat_ht}
                                                        onChange={(e) => setProduit({...produit, prix_achat_ht: parseFloat(e.target.value) || 0})}
                                                        required
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label>Prix de vente HT</Form.Label>
                                                    <Form.Control
                                                        type="number"
                                                        step="0.01"
                                                        min="0"
                                                        value={produit.prix_vente_ht}
                                                        onChange={(e) => setProduit({...produit, prix_vente_ht: parseFloat(e.target.value) || 0})}
                                                        required
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={4}>
                                                <Form.Group>
                                                    <Form.Label>TVA (%)</Form.Label>
                                                    <Form.Control
                                                        type="number"
                                                        step="0.1"
                                                        min="0"
                                                        max="100"
                                                        value={produit.tva_taux}
                                                        onChange={(e) => setProduit({...produit, tva_taux: parseFloat(e.target.value) || 0})}
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={4}>
                                                <Form.Group>
                                                    <Form.Label>CSS (%)</Form.Label>
                                                    <Form.Control
                                                        type="number"
                                                        step="0.1"
                                                        min="0"
                                                        max="100"
                                                        value={produit.css_taux}
                                                        onChange={(e) => setProduit({...produit, css_taux: parseFloat(e.target.value) || 0})}
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={4}>
                                                <Form.Group>
                                                    <Form.Label>Seuil d'alerte</Form.Label>
                                                    <Form.Control
                                                        type="number"
                                                        min="0"
                                                        value={produit.seuil_alerte}
                                                        onChange={(e) => setProduit({...produit, seuil_alerte: parseInt(e.target.value) || 0})}
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={4}>
                                                <Form.Group>
                                                    <Form.Label>Stock minimum</Form.Label>
                                                    <Form.Control
                                                        type="number"
                                                        min="0"
                                                        value={produit.stock_minimum}
                                                        onChange={(e) => setProduit({...produit, stock_minimum: parseInt(e.target.value) || 0})}
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={4}>
                                                <Form.Group>
                                                    <Form.Label>Stock maximum</Form.Label>
                                                    <Form.Control
                                                        type="number"
                                                        min="0"
                                                        value={produit.stock_maximum || ''}
                                                        onChange={(e) => setProduit({...produit, stock_maximum: e.target.value ? parseInt(e.target.value) : null})}
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={4}>
                                                <Form.Group>
                                                    <Form.Label>Emplacement</Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        value={produit.emplacement || ''}
                                                        onChange={(e) => setProduit({...produit, emplacement: e.target.value})}
                                                        placeholder="Ex: A-12-3"
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={12}>
                                                <hr />
                                                <div className="d-flex justify-content-between">
                                                    <Button 
                                                        variant="danger" 
                                                        onClick={() => setShowDeleteModal(true)}
                                                        disabled={saving}
                                                    >
                                                        <i className="ri-delete-bin-line me-2"></i>
                                                        Supprimer
                                                    </Button>
                                                    <div className="d-flex gap-3">
                                                        <Button 
                                                            variant="outline-secondary" 
                                                            onClick={() => navigate('/stock/produits')}
                                                        >
                                                            Annuler
                                                        </Button>
                                                        <Button 
                                                            type="submit" 
                                                            variant="primary"
                                                            disabled={saving}
                                                        >
                                                            {saving ? (
                                                                <>
                                                                    <Spinner size="sm" className="me-2" />
                                                                    Enregistrement...
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <i className="ri-save-line me-2"></i>
                                                                    Enregistrer
                                                                </>
                                                            )}
                                                        </Button>
                                                    </div>
                                                </div>
                                            </Col>
                                        </Row>
                                    </Form>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </div>
                <Footer />
            </div>

            {/* Modal de confirmation de suppression */}
            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title className="text-danger">
                        <i className="ri-alert-line me-2"></i>
                        Confirmation
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>Êtes-vous sûr de vouloir supprimer ce produit ?</p>
                    <p className="fw-semibold">{produit?.designation} ({produit?.reference})</p>
                    <p className="text-muted small">Cette action est irréversible.</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
                        Annuler
                    </Button>
                    <Button variant="danger" onClick={handleDelete} disabled={deleting}>
                        {deleting ? (
                            <>
                                <Spinner size="sm" className="me-2" />
                                Suppression...
                            </>
                        ) : (
                            'Confirmer la suppression'
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
        </React.Fragment>
    );
}